import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { DatabricksAccesses } from "./accesses.ts";
import { DatabricksComponents } from "./components.ts";
import { DATABRICKS_INTEGRATION_KEY } from "./constants.ts";

const DatabricksFilters = {
  // The Databricks account an admin role is granted in, so a rule can tell
  // two accounts' account admin apart.
  account: { keys: ["id"] },
  adminRole: { keys: ["name"] },
  catalog: { keys: ["name"] },
  privilege: { keys: ["name"] },
  schema: { keys: ["name"] },
  // The securable a privilege is granted on: a catalog, schema, table, volume
  // or function, by name and type.
  securable: { keys: ["name", "type"] },
  workspace: { keys: ["id", "name"] },
} as const satisfies AnyFilterSchema;
export type DatabricksFilters = typeof DatabricksFilters;

// Keyed by component, so a new component doesn't compile until it has one.
// The installer's "add an item" step asks for "the <identifier> of the
// <component> to install". A catalog's key is described on the field.
const IDENTIFIERS: Record<keyof typeof DatabricksComponents, string> = {
  connector: "AWS account",
  account: "ID",
  workspace: "ID",
  catalog: "key",
};
// `Registration` hands `identifier` any string, not just a component key.
const identifiers: Partial<Record<string, string>> = IDENTIFIERS;

export const DatabricksRegistration = Registration({
  accesses: DatabricksAccesses,
  category: "resource",
  components: DatabricksComponents,
  docs: {
    install: "databricks/installation",
    request: "databricks/requesting-access",
  },
  filters: DatabricksFilters,
  identifier: (component) => identifiers[component] ?? component,
  // Each component is named for what one of its items is.
  itemKind: (component) => component,
  label: "Databricks",
  logoUrl:
    "https://www.databricks.com/en-website-assets/icons/icon-192x192.png",
  maturity: "beta",
  requestType: "requestable",
  // Every access acts in a Databricks account, so the integration is usable
  // once one is installed.
  useMapping: [{ use: "iam_write", component: "account" }],
});

registerIntegration(DATABRICKS_INTEGRATION_KEY, DatabricksRegistration);
